import UiButton, { UiButtonStyle, UiButtonSize } from 'components/Ui/UiButton'
/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import HtmlTemplatePreview from 'components/EmailTemplates/HtmlTemplatePreview'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { EmailTemplateInputs, useEmailTemplateSchema } from '../schemas/useEmailTemplateSchema'
import { EmailTemplate } from 'types/entities/EmailTemplate'
import { getCompanyDisplayName } from 'utils/companyUtils'
import { useAppDispatch } from 'helpers/store'
import { EmailTemplatesAction } from 'store/emailTemplates/EmailTemplatesAction'
import { toast } from 'utils/toastUtils'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import UiInput from 'components/Ui/UiInput'
import UiTabSwitcher, { UiTabSwitcherItem } from 'components/Ui/UiTabSwitcher'
import HtmlTemplateEditor from 'components/EmailTemplates/HtmlTemplateEditor'
import { EnvelopeIcon, InformationCircleIcon, PaperClipIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { useTypedSelector } from 'helpers/reducers'
import EmailTemplateAttachmentItem from './EmailTemplateFormModalComponents/EmailTemplateAttachmentItem'
import EmailTemplatePlaceholderItem from './EmailTemplateFormModalComponents/EmailTemplatePlaceholderItem'
import { Offer } from 'types/entities/Offer'
import { Lead } from 'types/entities/Lead'
import { Company } from 'types/entities/Company'
import { useEmailPlaceholders } from 'utils/email/useEmailPlaceholders'
import { UiTabVariant } from 'types/enums/UiTabVariant'
import { EmailTemplateFormTab } from 'types/enums/specific/EmailTemplateFormTab'

interface EmailTemplateFormModalProps {
    modelName: string
    modelId: number
    template?: EmailTemplate
    onSuccess?: () => void
    context?: {
        offer?: Offer | null
        lead?: Lead | null
        company?: Company | null
    }
}

const EmailTemplateFormModal: React.FC<EmailTemplateFormModalProps> = ({
    modelName,
    modelId,
    template,
    onSuccess,
    context,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [activeTab, setActiveTab] = useState<EmailTemplateFormTab>(EmailTemplateFormTab.Creation)
    const [isLoading, setIsLoading] = useState(false)
    const [attachments, setAttachments] = useState<File[]>([])
    const [activeField, setActiveField] = useState<'subject' | 'content'>('content')
    const editorRef = useRef<HTMLTextAreaElement>(null)
    const subjectInputRef = useRef<HTMLInputElement | null>(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const currentUser = useTypedSelector((state) => state.sessionState.session)
    const { placeholders, resolvePlaceholders } = useEmailPlaceholders(context)

    const EmailTemplateSchema = useEmailTemplateSchema()
    const {
        register,
        handleSubmit,
        control,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<EmailTemplateInputs>({
        resolver: zodResolver(EmailTemplateSchema),
        mode: 'onTouched',
        defaultValues: {
            id: template?.id,
            model_id: modelId,
            model_name: modelName,
            name: template?.name || '',
            subject: template?.subject || '',
            content: template?.content || '',
            category: template?.category || '',
        },
    })

    const watchedContent = useWatch({ control, name: 'content' }) || ''
    const watchedSubject = useWatch({ control, name: 'subject' }) || ''

    const tabs: UiTabSwitcherItem[] = [
        { id: EmailTemplateFormTab.Creation, name: t('Creation') },
        { id: EmailTemplateFormTab.Preview, name: t('Live Preview') },
    ]

    const handlePlaceholderClick = (placeholder: string) => {
        if (activeField === 'subject' && subjectInputRef.current) {
            const input = subjectInputRef.current
            const start = input.selectionStart || 0
            const end = input.selectionEnd || 0
            const currentValue = getValues('subject') || ''

            const newValue = currentValue.substring(0, start) + placeholder + currentValue.substring(end)

            setValue('subject', newValue, { shouldValidate: true })

            setTimeout(() => {
                input.focus()
                input.setSelectionRange(start + placeholder.length, start + placeholder.length)
            }, 0)
            return
        }

        const input = editorRef.current
        if (input) {
            const start = input.selectionStart
            const end = input.selectionEnd
            const content = getValues('content') || ''
            setValue('content', content.slice(0, start) + placeholder + content.slice(end), {
                shouldDirty: true,
                shouldValidate: true,
            })
            requestAnimationFrame(() => {
                input.focus()
                input.setSelectionRange(start + placeholder.length, start + placeholder.length)
            })
        }
    }

    const getPreviewHtml = () => {
        return {
            subject: resolvePlaceholders(watchedSubject, { highlightMissing: false }),
            content: resolvePlaceholders(watchedContent, { highlightMissing: false, escapeValues: true }),
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files)
            setAttachments((prev) => [...prev, ...newFiles])
        }
    }

    const removeAttachment = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index))
    }

    /*
    const onExportHandler = async () => {
        // EML export would need a generic version of OfferEmailTemplatesAction.exportToEml
        // For now, let's keep it in mind
    }
    */

    const onSubmit = async (data: EmailTemplateInputs) => {
        setIsLoading(true)
        try {
            const result = await dispatch(EmailTemplatesAction.fetchCreateOrUpdateTemplate(data, attachments))
            if (result?.id) {
                await dispatch(EmailTemplatesAction.fetchTemplates(modelName, modelId))
                toast.success(data.id ? t('Template updated successfully') : t('Template created successfully'))
                onSuccess?.()
            } else {
                toast.error(t('Failed to save template'))
            }
        } catch (error) {
            console.error(error)
            toast.error(t('Failed to save template'))
        } finally {
            setIsLoading(false)
        }
    }

    const preview = getPreviewHtml()

    // Group placeholders by category
    const groupedPlaceholders = placeholders.reduce(
        (acc, p) => {
            if (!acc[p.category]) acc[p.category] = []
            acc[p.category].push(p)
            return acc
        },
        {} as Record<string, typeof placeholders>
    )

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="EmailTemplateFormModal h-full flex flex-col">
            <div className="px-6 py-4 bg-white/50 dark:bg-gray-900/50 backdrop-blur-md sticky top-0 z-10 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
                <UiTabSwitcher
                    items={tabs}
                    variant={UiTabVariant.Underline}
                    selected={activeTab}
                    onClick={(id) => setActiveTab(id as EmailTemplateFormTab)}
                />
                <div className="flex items-center gap-2 text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                    <SparklesIcon className="w-3 h-3 text-blue-500" />
                    {t('Dynamic Template')}
                </div>
            </div>

            <div className="flex-1 overflow-auto">
                {activeTab === EmailTemplateFormTab.Creation ? (
                    <div className="px-6 py-6 space-y-6">
                        <div className="flex flex-col gap-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <UiInput
                                        label={t('Template Name')}
                                        placeholder={t('e.g. Follow-up Email')}
                                        error={errors.name?.message}
                                        {...register('name')}
                                        className="bg-white! dark:bg-gray-900!"
                                    />
                                </div>
                                <div className="bg-blue-50/50 dark:bg-blue-900/10 rounded-xl p-4 border border-blue-100 dark:border-blue-900/30">
                                    <div className="flex items-center gap-2 mb-2">
                                        <InformationCircleIcon className="w-4 h-4 text-blue-500" />
                                        <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-tight">
                                            {t('Template Tip')}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-blue-600/80 dark:text-blue-400/80 leading-relaxed">
                                        {t(
                                            'Use placeholders to personalize your emails. Click on a placeholder below to insert it at the current cursor position in the subject or body.'
                                        )}
                                    </p>
                                </div>
                            </div>

                            <Controller
                                name="subject"
                                control={control}
                                render={({ field }) => (
                                    <UiInput
                                        label={t('Email Subject')}
                                        placeholder={t('Enter subject')}
                                        error={errors.subject?.message}
                                        {...field}
                                        ref={(e) => {
                                            field.ref(e)
                                            if (e) subjectInputRef.current = e
                                        }}
                                        onFocus={() => setActiveField('subject')}
                                        className="bg-white! dark:bg-gray-900! font-medium"
                                    />
                                )}
                            />

                            <div className="bg-white dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
                                <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 flex justify-between items-center">
                                    <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                        {t('Available Placeholders')}
                                    </h4>
                                    <span className="text-[10px] text-blue-500 font-medium px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 rounded-full border border-blue-100 dark:border-blue-900/50">
                                        {placeholders.length} {t('Detected')}
                                    </span>
                                </div>

                                <div className="p-4 flex flex-col gap-5 overflow-y-auto custom-scrollbar">
                                    {Object.entries(groupedPlaceholders).map(([category, items]) => (
                                        <div key={category} className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <div className="h-px bg-gray-100 dark:bg-gray-800 flex-1"></div>
                                                <div className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-tighter">
                                                    {category}
                                                </div>
                                                <div className="h-px bg-gray-100 dark:bg-gray-800 flex-1"></div>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {items.map((placeholder) => (
                                                    <EmailTemplatePlaceholderItem
                                                        key={placeholder.key}
                                                        placeholder={placeholder}
                                                        onClick={handlePlaceholderClick}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="AttachmentsSection space-y-3">
                                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-tight">
                                    {t('Attachments')}
                                </label>

                                <div className="flex flex-wrap gap-2.5 p-4 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-xl bg-gray-50/30 dark:bg-gray-900/30">
                                    {attachments.map((file, index) => (
                                        <EmailTemplateAttachmentItem
                                            key={`${file.name}-${index}`}
                                            file={file}
                                            index={index}
                                            onRemove={removeAttachment}
                                        />
                                    ))}

                                    <UiButton
                                        size={UiButtonSize.Small}
                                        style={UiButtonStyle.Secondary}
                                        icon={<PaperClipIcon className="w-4 h-4" />}
                                        onClick={() => fileInputRef.current?.click()}
                                        title={t('Attach File')}
                                    />
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        className="hidden"
                                        multiple
                                        onChange={handleFileChange}
                                    />
                                </div>
                            </div>

                            <Controller
                                name="content"
                                control={control}
                                render={({ field }) => (
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-tight">
                                            {t('Email Content')}
                                        </label>
                                        <HtmlTemplateEditor
                                            key={String(currentUser?.uid ?? currentUser?.user?.id ?? 'anonymous')}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            error={errors.content?.message}
                                            inputRef={(element) => {
                                                editorRef.current = element
                                                field.ref(element)
                                            }}
                                            onFocus={() => setActiveField('content')}
                                            storageScope={String(
                                                currentUser?.uid ?? currentUser?.user?.id ?? 'anonymous'
                                            )}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="px-6 py-6 h-full flex flex-col">
                        <div className="flex-1 bg-white dark:bg-gray-950 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-2xl flex flex-col relative">
                            {/* Window Header Decorative */}
                            <div className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 py-2.5 flex items-center justify-between">
                                <div className="flex gap-1.5">
                                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/80"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400/80"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/80"></div>
                                </div>
                                <div className="bg-white/80 dark:bg-gray-800/80 px-8 py-0.5 rounded-md border border-gray-100 dark:border-gray-700 text-[10px] text-gray-400 font-medium">
                                    {preview.subject || t('Draft')}
                                </div>
                                <div className="w-10"></div>
                            </div>

                            {/* Email Headers */}
                            <div className="p-6 border-b border-gray-100 dark:border-gray-900 flex flex-col gap-4">
                                <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                                    <span className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide text-right pr-4">
                                        {t('From')}
                                    </span>
                                    <div className="flex items-center gap-2.5 bg-gray-50 dark:bg-gray-900/50 px-3 py-1.5 rounded-lg border border-gray-100 dark:border-gray-800/50 w-fit">
                                        <div className="w-6 h-6 rounded-full bg-linear-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-[10px] text-white font-bold shadow-sm">
                                            {(currentUser?.user?.name || currentUser?.user?.email || 'U')[0]}
                                        </div>
                                        <span className="text-xs text-gray-700 dark:text-gray-200 font-semibold">
                                            {currentUser?.user?.name || currentUser?.user?.email || t('Current User')}
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                                    <span className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide text-right pr-4">
                                        {t('To')}
                                    </span>
                                    <div className="flex items-center gap-2.5 bg-blue-50/50 dark:bg-blue-900/10 px-3 py-1.5 rounded-lg border border-blue-100/50 dark:border-blue-900/20 w-fit">
                                        <div className="w-6 h-6 rounded-full bg-linear-to-tr from-emerald-500 to-teal-500 flex items-center justify-center text-[10px] text-white font-bold shadow-sm">
                                            {(getCompanyDisplayName(context?.company) || context?.lead?.name || 'C')[0]}
                                        </div>
                                        <span className="text-xs text-blue-700 dark:text-blue-300 font-semibold">
                                            {getCompanyDisplayName(context?.company) ||
                                                context?.lead?.name ||
                                                t('Recipient')}
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-[80px_1fr] items-start gap-2">
                                    <span className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide text-right pr-4 pt-1">
                                        {t('Subject')}
                                    </span>
                                    <span className="text-sm text-gray-900 dark:text-gray-100 font-extrabold leading-tight">
                                        {preview.subject || (
                                            <span className="text-gray-300 font-normal italic">
                                                ({t('No subject')})
                                            </span>
                                        )}
                                    </span>
                                </div>

                                {attachments.length > 0 && (
                                    <div className="grid grid-cols-[80px_1fr] items-center gap-2 pt-2">
                                        <span className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide text-right pr-4">
                                            {t('Files')}
                                        </span>
                                        <div className="flex flex-wrap gap-2">
                                            {attachments.map((file, index) => (
                                                <EmailTemplateAttachmentItem
                                                    key={`${file.name}-${index}`}
                                                    file={file}
                                                    index={index}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Email Body Content */}
                            <div className="p-10 flex-1 overflow-auto bg-white dark:bg-gray-950">
                                <div className="max-w-3xl mx-auto">
                                    {preview.content ? (
                                        <HtmlTemplatePreview title={t('Live Preview')} html={preview.content} />
                                    ) : (
                                        <div className="flex flex-col items-center justify-center h-64 text-gray-300 dark:text-gray-700 gap-4">
                                            <EnvelopeIcon className="w-16 h-16 opacity-20" />
                                            <p className="italic text-sm">
                                                {t('Write something to see a live preview...')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Decorative Footer */}
                            <div className="h-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800"></div>
                        </div>
                    </div>
                )}
            </div>

            <div className="px-6 py-4 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-md border-t border-gray-100 dark:border-gray-800">
                <UiFormActionsBar
                    primaryText={template ? t('Save Changes') : t('Create Template')}
                    onPrimary={handleSubmit(onSubmit)}
                    secondaryText={t('Cancel')}
                    onSecondary={onSuccess}
                    loading={isLoading}
                />
            </div>
        </form>
    )
}

export default EmailTemplateFormModal
